import React from 'react';
import styled, { css } from 'styled-components';
import { Button } from '@zocdoc-frontend-common/components-button';
import ErrorMessage from '@zocdoc-frontend-common/components-errormessage';
import Form from '@zocdoc-frontend-common/components-form';
import Textbox from '@zocdoc-frontend-common/components-textbox';
import { breakpoint } from '@zocdoc-frontend-common/styles-grid';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import { red10, red70, slate30 } from '@zocdoc-mezzanine/foundations-colors';
import {
    elementNames,
    errorNames,
} from 'src/Booking/components/CreateUsernameForm/constants';

/**
 *
 * @param {boolean} emailAlreadyInUseError
 * @param {Object} errors
 * @param {function} handleBlur
 * @param {function} handleChange
 * @param {function} handleClickSubmit
 * @param {function} handleFocus
 * @param {Object} highlightedFields
 * @param {boolean} loading
 * @param {function} onClickLoginLink
 * @param {boolean} submitted
 * @param {Object} values
 * @returns {JSX.Element}
 * @constructor
 */
const CreateUsernameFormView = ({
    emailAlreadyInUseError,
    errors,
    handleBlur,
    handleChange,
    handleClickSubmit,
    handleFocus,
    highlightedFields,
    inlineEmailLoading,
    onClickLoginLink,
    submitted,
    values,
}) => {
    return (
        <CreateUsernameForm
            onSubmit={handleClickSubmit}
            data-test="create-username-form"
        >
            <Section>
                <Heading>
                    To continue booking, finish setting up your profile.
                </Heading>
                <Subheading>
                    We’ll keep your information handy for future appointments.
                </Subheading>
            </Section>
            <Section>
                <SectionTitle id="email-title">Email</SectionTitle>
                <InputRow>
                    <StyledTextbox
                        aria-labelledby="email-title"
                        autoComplete="username"
                        className="exclude-from-playback"
                        data-test="email"
                        data-uem-id="email"
                        name={elementNames.email}
                        inputMode="email"
                        invalid={Boolean(highlightedFields[elementNames.email])}
                        value={values[elementNames.email]}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        tabIndex="0"
                        type="email"
                    ></StyledTextbox>
                </InputRow>
                {errors[errorNames.emailError] && (
                    <ErrorMessage data-test="email-error-empty">
                        {values[elementNames.email]
                            ? 'Please type a valid email.'
                            : 'Please type an email.'}
                    </ErrorMessage>
                )}
                {!errors[errorNames.emailError] && emailAlreadyInUseError && (
                    <TakenHintContainer
                        submitted={submitted}
                        data-test="email-taken-container"
                    >
                        <CTA>
                            <Text data-test="email-taken-text">
                                Looks like you already have an account.
                            </Text>
                            <LoginLink
                                onClick={onClickLoginLink}
                                data-test="inline-email-login-link"
                            >
                                Log in now
                            </LoginLink>
                        </CTA>
                    </TakenHintContainer>
                )}
            </Section>

            <ButtonContainer>
                <StyledButton
                    data-test="submit-button"
                    disabled={inlineEmailLoading}
                    type="submit"
                >
                    Continue
                </StyledButton>
            </ButtonContainer>
        </CreateUsernameForm>
    );
};

const CreateUsernameForm = styled(Form)`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin: auto;
    /* Set spacing between adjacent divs that are direct children */
    > div + div {
        margin-top: 24px;
    }

    /* Override FEC components to use Mezzanine colors */
    ${ErrorMessage} {
        color: ${red70};
    }
`;

const Heading = styled.h1`
    font: 28px/40px ${FontFamily.SharpSansBold};
    ${breakpoint.small} {
        font-size: 32px;
    }
    margin-bottom: 12px;
`;

const Subheading = styled.h2`
    font: 15px/24px ${FontFamily.SharpSansMedium};
    ${breakpoint.small} {
        font-size: 16px;
    }
    margin-bottom: 4px;
`;

const InputRow = styled.div`
    display: flex;
    width: 100%;
    > div {
        flex-basis: 100%;
    }
    > div:not(:last-of-type) > input {
        border-right-width: 0px;
    }
`;

const SectionTitle = styled.label`
    font: 15px/24px ${FontFamily.SharpSansSemibold};
    margin-bottom: 4px;
`;

const Section = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const StyledTextbox = styled(Textbox)`
    border-color: ${slate30};
    min-width: 0;
    ${(p) =>
        p.invalid &&
        css`
            border-color: ${red70};
            border-right-width: 1px !important;
        `};
`;

const ButtonContainer = styled.div`
    margin-top: 24px;
    margin-bottom: 40px;

    ${breakpoint.small} {
        margin-bottom: 72px;
    }
    width: 100%;

    /* space between button and error text*/
    > * + * {
        margin-top: 3px;
    }
`;

const StyledButton = styled(Button)`
    box-sizing: border-box;
    font: 16px/28px ${FontFamily.SharpSansSemibold};
    ${breakpoint.small} {
        font-size: 18px;
    }
    width: 100%;
    padding: 9px 15px 9px 15px;
`;

const TakenHintContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
    border: 1px solid;
    border-color: ${red70};
    margin-top: 8px;
    padding: 10px 0px 10px 16px;
    font: 15px ${FontFamily.SharpSansMedium};
    line-height: 24px;
    background-color: ${red10};
    ${breakpoint.medium} {
        height: 48px;
    }
`;

const CTA = styled.span`
    align-self: center;
`;

const Text = styled.span`
    padding-right: 4px;
`;

const LoginLink = styled.a`
    display: inline-block;
    border-bottom: 2px dotted;
    color: rgb(43, 191, 237);
    flex-basis: 100px;
    cursor: pointer;
`;

export default CreateUsernameFormView;
